/**
 * The all-surface preview gallery (#25 / R5.3).
 *
 * The P05 gate requires that every screen preview loads. This is the index that makes that
 * checkable by a person in about a minute, and it is deliberately plain: no artwork, no theme of
 * its own, no cleverness. It lists what exists, says plainly what does NOT, and links to each.
 *
 * It is NOT a product surface. It carries the labels that stop a preview screenshot being mistaken
 * for the running application.
 */
import React from 'react';
import { Link } from 'react-router-dom';

import { GALLERY_SURFACES, UNBUILT_ROUTES } from './routeTable.js';

const wrap = { padding: '2rem 1.5rem', maxWidth: '58rem', margin: '0 auto' };
const grid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(13rem, 1fr))',
  gap: '0.75rem',
  padding: 0,
  margin: '0 0 2rem',
  listStyle: 'none',
};
const card = {
  display: 'block',
  minHeight: '44px',
  padding: '0.75rem 1rem',
  border: '1px solid var(--line, #bdb1a2)',
  borderRadius: 'var(--r-sm, 8px)',
  color: 'inherit',
  textDecoration: 'none',
};

export default function FixtureGallery() {
  // `GALLERY_SURFACES` is the ONE definition, shared with the `#/gallery/:name` route so the index
  // and the thing it links to cannot disagree about what a gallery surface is.
  const surfaces = GALLERY_SURFACES;

  return (
    <main style={wrap} data-route-state="gallery">
      <h1>Surface preview gallery</h1>
      <p>
        Every screen below renders from <strong>fixture data</strong>. Nothing here reaches a server,
        and nothing here is evidence that a journey works. It exists so each surface can be opened
        and looked at.
      </p>

      <h2>{surfaces.length} recovered surfaces</h2>
      <ul style={grid}>
        {surfaces.map((route) => (
          // Keyed on the NAME: a preview-only surface (#583, the operator mockup) has no path.
          <li key={route.name}>
            {/*
              `/gallery/<name>`, NOT `route.path`. A card that linked to the live path stopped
              previewing anything the moment that path got a container: `App.jsx` resolves it to
              `LIVE_CONTAINERS[route.path]`, which issues its GET and renders server state under a
              page that promises fixtures. Measured on the deployed preview 2026-09-11 — the Today
              card landed on `#/today` with no preview notice at all.
            */}
            <Link style={card} to={`/gallery/${route.name}`}>
              {route.surface}
              <br />
              <code style={{ fontSize: '0.85em', opacity: 0.75 }}>#/gallery/{route.name}</code>
            </Link>
          </li>
        ))}
      </ul>

      <h2>Named gaps, shown rather than hidden</h2>
      <p>
        These link targets exist in the recovered components but have no surface. Each says which
        delivery task owns it instead of failing as a broken link.
      </p>
      <ul style={grid}>
        {UNBUILT_ROUTES.map((route) => (
          <li key={route.path}>
            <Link style={card} to={route.path}>
              {route.label} — not built
              <br />
              <code style={{ fontSize: '0.85em', opacity: 0.75 }}>owned by {route.ownedBy}</code>
            </Link>
          </li>
        ))}
        <li>
          <Link style={card} to="/no-such-route">
            Unknown route boundary
            <br />
            <code style={{ fontSize: '0.85em', opacity: 0.75 }}>#/no-such-route</code>
          </Link>
        </li>
      </ul>

      <h2>What this gallery does not prove</h2>
      <ul>
        <li>That any journey works. There is no server behind these screens yet.</li>
        <li>
          That the artwork is accepted. Visual fidelity is verified independently in #38 (R9.4).
        </li>
        <li>That the data is real. Every name, amount and avatar below is fixture data.</li>
      </ul>
    </main>
  );
}
